import { haversineKm } from '../maps/distance';
import type { LatLng } from '../maps/types';
import {
  minutesToTimeString,
  timeStringToMinutes,
} from '../utils/scheduleConflict';
import { routeOdKey } from './routeOdKey';
import type { TravelTimeOptions } from './types';

const DEFAULT_SPEED_KMH = 35;
const DEFAULT_ROAD_FACTOR = 1.25;

export { timeStringToMinutes, minutesToTimeString };
export { routeOdKey } from './routeOdKey';

/** Haversine × roadFactor ÷ speed (always available; used for static mode and directions fallback). */
export function estimateStaticTravelMinutes(
  from: LatLng,
  to: LatLng,
  options: TravelTimeOptions = {}
): number {
  const speedKmh = options.averageSpeedKmh ?? DEFAULT_SPEED_KMH;
  const roadFactor = options.roadFactor ?? DEFAULT_ROAD_FACTOR;
  if (speedKmh <= 0) return 0;

  const km = haversineKm(from, to) * roadFactor;
  if (km < 0.01) return 0;
  return Math.max(1, Math.ceil((km / speedKmh) * 60));
}

/**
 * Deadhead minutes between two points.
 * `travelTimeMode=directions` uses prefetched Routes values when present; otherwise static.
 */
export function estimateTravelMinutes(
  from: LatLng,
  to: LatLng,
  options: TravelTimeOptions = {}
): number {
  if (options.travelTimeMode === 'directions' && options.directionsMinutes) {
    const keyed = options.directionsMinutes[routeOdKey(from, to)];
    if (typeof keyed === 'number' && Number.isFinite(keyed) && keyed >= 0) {
      if (keyed === 0) return 0;
      return Math.max(1, Math.ceil(keyed));
    }
  }
  return estimateStaticTravelMinutes(from, to, options);
}

export function estimateTravelKm(
  from: LatLng,
  to: LatLng,
  options: TravelTimeOptions = {}
): number {
  const roadFactor = options.roadFactor ?? DEFAULT_ROAD_FACTOR;
  return haversineKm(from, to) * roadFactor;
}

export interface SchedulingSettings {
  travel_time_mode: 'static' | 'directions';
  static_average_speed_kmh: number;
  travel_road_factor: number;
}

export const DEFAULT_SCHEDULING_SETTINGS: SchedulingSettings = {
  travel_time_mode: 'static',
  static_average_speed_kmh: DEFAULT_SPEED_KMH,
  travel_road_factor: DEFAULT_ROAD_FACTOR,
};

export function parseSchedulingSettings(
  raw: Record<string, unknown> | null | undefined
): SchedulingSettings {
  if (!raw) return { ...DEFAULT_SCHEDULING_SETTINGS };
  return {
    travel_time_mode: raw.travel_time_mode === 'directions' ? 'directions' : 'static',
    static_average_speed_kmh:
      typeof raw.static_average_speed_kmh === 'number'
        ? raw.static_average_speed_kmh
        : DEFAULT_SPEED_KMH,
    travel_road_factor:
      typeof raw.travel_road_factor === 'number' ? raw.travel_road_factor : DEFAULT_ROAD_FACTOR,
  };
}

export function schedulingSettingsToTravelOptions(
  settings: SchedulingSettings
): TravelTimeOptions {
  return {
    averageSpeedKmh: settings.static_average_speed_kmh,
    roadFactor: settings.travel_road_factor,
    travelTimeMode: settings.travel_time_mode,
  };
}
